import { tvModeActive } from '@classes/app_url_params'
import { authIsExpired } from '@classes/auth_service'
import Loader from '@components/Loader'
import { getAllPageNames, getPages, MAIN_ADMIN_ROUTE } from '@config'
import { ROUTES } from '@constants/app_routes'
import type { AppAuthState, AppState } from '@constants/types'
import { isAnyOfPagesEnabled } from '@constants/utils'
import { useYisNavigate } from '@hooks/useYisNavigate'
import { StyledEngineProvider } from '@mui/material'
import { PageIds } from '@shared_types/pages'
import { updateAuth } from '@store/actions'
import { isArray } from 'lodash'
import type { JSX, LazyExoticComponent } from 'react'
import React, { type CSSProperties, lazy, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useLocation } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

const AdminPage = lazy(() => import('@containers/admin/index'))

interface FoundRoute {
  path: string
  component: LazyExoticComponent<() => JSX.Element>
}

const getRoutes = (page: PageIds): FoundRoute | FoundRoute[] | undefined => {
  switch (page) {
    case PageIds.HOME: return { path: ROUTES.HOME, component: lazy(() => import('@containers/homePage/HomePage')) }
    case PageIds.ECO_SCORE: return { path: ROUTES.ECO_SCORE, component: lazy(() => import('@containers/EcoScorePage')) }
    case PageIds.THREE_D: return { path: ROUTES.THREE_D, component: lazy(() => import('@containers/ThreeDPage/ThreeDPage')) }
    case PageIds.PROPELLERS: return { path: ROUTES.PROPELLERS, component: lazy(() => import('./containers/mechanical/PropellersPage')) }
    case PageIds.RUDDER: return { path: ROUTES.RUDDER, component: lazy(() => import('@containers/mechanical/RudderPage')) }
    case PageIds.THRUSTER: return { path: ROUTES.THRUSTER, component: lazy(() => import('@containers/mechanical/ThrusterPage')) }
    case PageIds.GEARBOX: return { path: ROUTES.GEARBOX, component: lazy(() => import('@containers/mechanical/GearboxPage')) }
    case PageIds.CHILLER: return { path: ROUTES.CHILLER, component: lazy(() => import('@containers/mechanical/ChillerPage')) }
    case PageIds.ENGINES: return { path: ROUTES.ENGINES, component: lazy(() => import('@containers/mechanical/EnginesPage')) }
    case PageIds.BATTERY: return { path: ROUTES.BATTERY, component: lazy(() => import('@containers/mechanical/BatteryPage')) }
    case PageIds.SAFETY_PLAN: return { path: ROUTES.SAFETY_PLAN, component: lazy(() => import('@containers/SafetyPlanPage')) }
    case PageIds.YACHT_PAGE: return { path: ROUTES.YACHT_PAGE, component: lazy(() => import('@containers/YachtPage')) }
    case PageIds.WATERMAKERS: return { path: ROUTES.WATERMAKERS, component: lazy(() => import('@containers/mechanical/WatermakersPage')) }
    case PageIds.GENERATORS: return { path: ROUTES.GENERATORS, component: lazy(() => import('@containers/mechanical/ElectricPowerPage')) }
    case PageIds.TANKS: return { path: ROUTES.TANKS, component: lazy(() => import('@containers/mechanical/TanksPage')) }
    case PageIds.STABILIZERS: return { path: ROUTES.STABILIZERS, component: lazy(() => import('@containers/mechanical/StabilizersPage')) }
    case PageIds.TIMES: return { path: ROUTES.TIMES, component: lazy(() => import('@containers/times/TimesPage')) }
    case PageIds.SPEED_DISTANCE: return { path: ROUTES.SPEED_DISTANCE, component: lazy(() => import('@containers/SpeedDistancePage/SpeedDistancePage')) }
    case PageIds.DEPTH: return { path: ROUTES.DEPTH, component: lazy(() => import('@containers/weatherPage/DepthPage')) }
    case PageIds.BAROMETRIC_PRESSURE: return { path: ROUTES.BAROMETRIC_PRESSURE, component: lazy(() => import('@containers/weatherPage/BarometricPressurePage')) }
    case PageIds.WIND: return { path: ROUTES.WIND, component: lazy(() => import('@containers/weatherPage/WindPage')) }
    case PageIds.MAP: return { path: ROUTES.MAP, component: lazy(() => import('@containers/MapPage')) }
    case PageIds.VIDEO: return { path: ROUTES.VIDEO, component: lazy(() => import('@containers/DemoPage/DemoPage')) }
    case PageIds.DETAILS: return { path: ROUTES.DETAILS, component: lazy(() => import('@containers/mechanical/MechanicalComponentsLayoutPage')) }
    case PageIds.INFO: return { path: ROUTES.INFO, component: lazy(() => import('@containers/shipInfoPage/ShipInfoPage')) }
    case PageIds.WEATHER: return { path: ROUTES.WEATHER, component: lazy(() => import('@containers/weather/Weather')) }
    case PageIds.DISTANCE: return { path: ROUTES.DISTANCE, component: lazy(() => import('@containers/DistancePage/DistancePage')) }
    case PageIds.MEKKAH: return { path: ROUTES.MEKKAH, component: lazy(() => import('@containers/MekkahPage')) }
    case PageIds.PINCODE: return { path: ROUTES.PINCODE, component: lazy(() => import('@containers/PinCode')) }
    case PageIds.TEMPERATURES: return { path: ROUTES.TEMPERATURES, component: lazy(() => import('@containers/weatherPage/TemperaturesPage')) }
    case PageIds.CALENDAR: return { path: ROUTES.CALENDAR, component: lazy(() => import('@containers/CalendarPage')) }
    case PageIds.CREW: return { path: ROUTES.CREW, component: lazy(() => import('@containers/CrewPage')) }
    case PageIds.TRIP: return { path: ROUTES.TRIP, component: lazy(() => import('@containers/TripPage')) }
    case PageIds.SAT_ANTENNAS:
      return [
        { path: ROUTES.SAT_ANTENNAS, component: lazy(() => import('./containers/mechanical/SatAntennasPage')) },
        { path: ROUTES.SAT_ANTENNA, component: lazy(() => import('./containers/mechanical/SatAntennasPage')) }
      ]
    case PageIds.GALLERY:
      return [
        { path: ROUTES.GALLERIES, component: lazy(() => import('@containers/GalleryPage/GalleryPage')) },
        { path: ROUTES.GALLERY, component: lazy(() => import('@containers/GalleryPage/GalleryPage')) }
      ]
    case PageIds.INFO_CHANNEL:
      return [
        { path: ROUTES.INFO_CHANNELS, component: lazy(() => import('@containers/InfoChannel')) },
        { path: ROUTES.INFO_CHANNEL, component: lazy(() => import('@containers/InfoChannel')) }
      ]
    case PageIds.PDF:
      return [
        { path: ROUTES.PDFS, component: lazy(() => import('@containers/PdfPage')) },
        { path: ROUTES.PDF, component: lazy(() => import('@containers/PdfPage')) }
      ]
    case PageIds.PRESENTATIONS:
      return { path: ROUTES.PRESENTATIONS, component: lazy(() => import('@containers/Presentations')) }
    default:
      return undefined
  }
}

export default function Router () {
  const navigate = useYisNavigate()
  const location = useLocation()

  const auth = useSelector((appState: AppState) => appState.auth)
  const tvMode = tvModeActive()
  const ovalMode = useSelector((appState: AppState) => appState.ovalMode.enabled)
  const ovalWidth = useSelector((appState: AppState) => appState.ovalMode.width)
  const dispatch = useDispatch()

  const prevProps = useRef<AppAuthState | null>(null)
  const [paths, setPaths] = useState<FoundRoute[]>([])

  const transitionTime = 730

  const classNames = tvMode || ovalMode ? 'fadepage' : 'slide'
  const wrapperStyle: CSSProperties = ovalMode ? { padding: `0px ${ovalWidth}` } : {}

  const logout = () => {
    localStorage.removeItem('pincode')
    localStorage.removeItem('expiresIn')
    dispatch(updateAuth('auth', { name: 'isAuthenticated', value: false }))
  }

  useEffect(() => {
    const availablePages = getAllPageNames()
    const disabledPages = getPages().filter(p => p.page && p.disabled)

    availablePages.forEach(page => {
      const route = getRoutes(page.toUpperCase() as PageIds)
      if (route && disabledPages.findIndex(dp => dp.key === page) === -1) {
        // change it
        if (tvMode && page !== PageIds.PINCODE) {
          const routesToSet = isArray(route) ? route : [route]
          setPaths(prevState => [...prevState, ...routesToSet])
        } else if (!tvMode) {
          const routesToSet = isArray(route) ? route : [route]
          setPaths(prevState => [...prevState, ...routesToSet])
        }
      }
    })
  }, [])

  useEffect(() => {
    const savedTime = localStorage.getItem('expiresIn')
    if (!savedTime || authIsExpired(+savedTime)) {
      logout()
    }
  }, [])

  useEffect(() => {
    const prevAuth = prevProps.current

    const shouldRedirectToPin
      = prevAuth !== auth
      && !tvMode
      && !auth.isAuthenticated
      && auth.showAuthForm
      && !location.pathname.startsWith(MAIN_ADMIN_ROUTE)
      && isAnyOfPagesEnabled([ROUTES.PINCODE])

    if (shouldRedirectToPin) {
      navigate(ROUTES.PINCODE)
    }

    prevProps.current = auth
  }, [auth, tvMode])

  return <StyledEngineProvider injectFirst>
    <TransitionGroup className="route-wrapper">
      <React.Suspense fallback={<Loader center />}>
        <Routes>
          {paths.map(r => <Route key={r.path} path={r.path} element={<CSSTransition
            in={true}
            timeout={transitionTime}
            classNames={classNames}
            unmountOnExit>
            <div className="container-100 width-100" style={wrapperStyle}>
              {React.createElement(r.component)}
            </div>
          </CSSTransition>}
          />)}
          <Route path={ROUTES.ADMIN} element={<AdminPage />} />
        </Routes>
      </React.Suspense>
    </TransitionGroup>
  </StyledEngineProvider>
}
